import { Fragment, useEffect, useLayoutEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { ScanLine, X } from 'lucide-react';
import { MAX_IKON_MODULU, barvaSekce, modulStranky, smiNaStranku, viditelneSekce } from '../../lib/moduly';
import { TRIDA_PRUHLEDNY_STATUS_BAR } from '../../lib/iosInstalace';
import { ROZOSTRENI_NAVIGACE } from '../../lib/uiTokens';
import Odznak from '../ui/Odznak';
import { SkloKruh } from '../ui/Sklo';

const BRAND = '#FF8A00';

/**
 * MobilniMenu — celoobrazovkové menu modulů na mobilu, otevírá ho slot
 * „Moduly" ve spodní liště.
 *
 * Jazyk iOS 26 (od 2026-09-29, varianta A z návrhu „Menu modulů ve skle"):
 * **ikony jako na ploše telefonu** — skleněný čtverec tónovaný barvou sekce
 * a pod ním název, bez karet kolem. Nahoře tichý řádek s verzí, pod ním
 * sekce jako lišta na desktopu (do MAX_IKON_MODULU modulů bez nadpisů).
 * **Dole u palce rychlé akce a zavření** — kulatá tlačítka (Nahrát doklad, ✕).
 * **Pevné rozvržení** (od 2026-10-04): menu se neroluje, mřížka se do displeje
 * vejde zmenšením. Hledání tu proto taky není — hledat není co.
 *
 * **Menu překrývá lištu** (`z-70`, nad lištou na 59). Dřív leželo pod ní
 * s odůvodněním, že je to stránka jako každá jiná a dá se z něj odejít
 * kterýmkoli slotem — jenže menu není stránka, je to rozcestník: otevřu ho,
 * abych si vybral modul, a pak ho zavřu. Přepnout z něj rovnou do Zpráv je
 * okrajový případ, kdežto zpod lišty si bralo 86 px, tedy skoro celý řádek
 * dlaždic. Odejde se z něj skleněným kruhem ✕ dole (`SkloKruh`).
 *
 * **Menu dělá jednu věc: otevírá moduly.** Účet tu byl — řádek se jménem
 * nahoře a pod ním vysouvací list s motivem, verzí a odhlášením — a nikdo ho
 * tu nehledal: obrazovka se jmenuje Moduly. Profil se tím navíc dělil na dvě
 * místa (vizitka a dokumenty v sekci Přehledu, nastavení tady). Všechno osobní
 * je teď v jedné sekci **Domů → Můj profil**.
 *
 * **Co má v liště vlastní slot, tady není.** Zprávy mají v katalogu
 * `slotVListe` a z mřížky vypadnou — jinak by byly na obrazovce dvakrát
 * a jejich počet ve dvou odznacích. Rychlé akce dole jsou jen úkony, které
 * lišta nemá (Nahrát doklad), ne zkratky na její sloty. Odznaky dlaždic
 * zůstávají tomu, co čeká uvnitř modulů — nepodepsané směrnice, nedokončená školení.
 *
 * @param {Object} props
 * @param {boolean} props.open
 * @param {() => void} props.onClose
 * @param {Object} props.user
 * @param {string} props.currentPage
 * @param {(pageId: string) => void} props.onNavigate
 * @param {Object} [props.pocty] - { id stránky / modulu: počet } — zpravy, aktivita, bezpecnost, skoleni…
 * @param {boolean} props.isDark
 */
export default function MobilniMenu({ open, onClose, user, currentPage, onNavigate, pocty = {}, isDark = true }) {
    useEffect(() => {
        if (!open) return;
        const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [open, onClose]);

    // Pevné rozvržení: menu se nikdy neroluje. Když se mřížka na displej
    // nevejde (menší telefon, větší písmo, výřez a domovské gesto na iPhonu),
    // zmenší se celá přes `zoom` přesně tak, aby se vešla. Měří se při každém
    // vykreslení i změně velikosti — počet modulů se mění s přístupy.
    const plochaRef = useRef(null);
    const obsahRef = useRef(null);
    useLayoutEffect(() => {
        const plocha = plochaRef.current;
        const obsah = obsahRef.current;
        if (!open || !plocha || !obsah) return undefined;
        const prepocet = () => {
            obsah.style.zoom = '1';
            const prirozena = obsah.getBoundingClientRect().height;
            const k = prirozena > plocha.clientHeight ? plocha.clientHeight / prirozena : 1;
            obsah.style.zoom = k < 1 ? String(Math.floor(k * 1000) / 1000) : '';
        };
        prepocet();
        const ro = new ResizeObserver(prepocet);
        ro.observe(plocha);
        return () => ro.disconnect();
    });

    if (!open) return null;

    // Moduly s vlastním slotem v liště (Zprávy) do mřížky nepatří — jsou na
    // jedno klepnutí dole a tady by byly podruhé.
    const sekce = viditelneSekce(user)
        .map((s) => ({ ...s, items: s.items.filter((i) => !i.slotVListe) }))
        .filter((s) => s.items.length > 0);
    const pocet = sekce.reduce((n, s) => n + s.items.length, 0);
    // Nad `MAX_IKON_MODULU` (6) se kreslí nadpisy sekcí — a od stejné hranice
    // i **hustší mřížka**. Kdo má pár modulů, dostane velké dlaždice a pohodlí;
    // kdo jich má devatenáct, se jinak proscrolluje 1 076 px na 750px displeji.
    const sNadpisy = pocet > MAX_IKON_MODULU;
    const husta = sNadpisy;

    // Pozor: tady a níž **žádné hooky**. Komponenta má nad tímhle místem
    // `if (!open) return null`, takže hook za ním by se při zavřeném menu
    // nezavolal a při otevření přibyl — React na to spadne (#310, „rendered
    // more hooks than during the previous render"). Memoizace by tu stejně
    // nic neušetřila: jde o dvacet modulů a menu se počítá jen otevřené.
    //
    const vsechnyModuly = sekce.flatMap((s) => s.items.map((i) => ({ item: i, s })));

    // Rychlé akce — úkony na jedno klepnutí, které nejsou modulem. Kulatá
    // tlačítka dole vedle ✕ (od 2026-10-04; předtím řádek dlaždic nahoře).
    const rychleAkce = [
        { id: 'doklady-mobil-upload', label: 'Nahrát doklad', Icon: ScanLine },
    ].filter((a) => smiNaStranku(user, a.id));

    const aktivniModul = modulStranky(currentPage)?.item.id;
    // Verze aplikace patří k programu, ne k člověku — proto tady, ne v profilu.
    const verze = document.querySelector('meta[name="app-version"]')?.content;
    // Aplikace přidaná na plochu ještě s průhledným stavovým řádkem — iOS ho
    // čte jen při přidání, oprava chce reinstalaci.
    const staraInstalace = document.documentElement.classList.contains(TRIDA_PRUHLEDNY_STATUS_BAR);

    // Sklo: menu je průhledná tónovaná vrstva nad rozmazanou stránkou, s vlastní
    // brandovou září (oranžová vlevo dole, fialová vpravo nahoře, tyrkysová
    // vpravo) — barvy prosvítají, i když je pod menu rušný obsah. Dlaždice
    // a ovládací prvky jsou skleněné destičky s odleskem na horní hraně.
    // Nahoře začíná barvou `--horni-hrana` stejným přechodem jako pozadí
    // stránky (app.css `--horni-prechod`), takže pruh pod stavovým řádkem
    // s menu splyne a u horní hrany se nekreslí dva gradienty přes sebe.
    const pozadiMenu = isDark
        ? 'var(--horni-prechod), '
          + 'radial-gradient(95% 55% at 0% 100%, rgba(255,138,0,0.22) 0%, transparent 70%), '
          + 'radial-gradient(85% 50% at 100% 0%, rgba(151,90,205,0.22) 0%, transparent 70%), '
          + 'radial-gradient(70% 45% at 95% 70%, rgba(12,192,223,0.16) 0%, transparent 70%), '
          + 'rgba(10,10,14,0.5)'
        : 'var(--horni-prechod), '
          + 'radial-gradient(95% 55% at 0% 100%, rgba(255,138,0,0.24) 0%, transparent 70%), '
          + 'radial-gradient(85% 50% at 100% 0%, rgba(151,90,205,0.22) 0%, transparent 70%), '
          + 'radial-gradient(70% 45% at 95% 70%, rgba(12,192,223,0.18) 0%, transparent 70%), '
          + 'rgba(245,245,247,0.55)';

    const jdi = (id, params) => { onNavigate(id, params); onClose?.(); };

    // Odznak v rohu dlaždice — sdílená komponenta, stejná jako v liště.
    // Dřív byl prstenec v barvě `--surface-base`, jenže dlaždice leží na skle
    // nad brandovou září, ne na té barvě, takže z prstence byl viditelný
    // kroužek.
    const odznak = (pocet) => (
        <Odznak pocet={pocet} isDark={isDark}
            style={{ position: 'absolute', top: -6, right: -6 }} />
    );

    // Čtyři ikony v řadě, u krátkého menu tři větší. Cíl (ikona + název) má
    // i v husté mřížce přes 70 × 80 px, hluboko nad minimem 44 px z normy.
    const mrizka = {
        display: 'grid',
        gridTemplateColumns: `repeat(${husta ? 4 : 3}, minmax(0, 1fr))`,
        gap: husta ? '10px 6px' : '16px 8px',
    };
    const strana = husta ? 52 : 64;

    // `color` je sytá barva sekce (podbarvení ikony), `popredi` tón čitelný
    // na aktuálním motivu (název, ikona, rámeček aktivní dlaždice).
    const dlazdice = (item, color, popredi = color, klic = item.id) => {
        const aktivni = aktivniModul === item.id;
        const Icon = item.Icon;
        const ceka = item.pripravovany;
        return (
            <button
                key={klic}
                type="button"
                onClick={ceka ? undefined : () => jdi(item.id)}
                disabled={ceka}
                aria-label={ceka ? `${item.label} — připravujeme` : item.label}
                aria-current={aktivni ? 'page' : undefined}
                style={{
                    ...(ceka ? { opacity: 0.45, cursor: 'default' } : null),
                    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: husta ? 5 : 6,
                    minWidth: 0, padding: 0, border: 'none', background: 'none',
                    cursor: 'pointer', font: 'inherit', color: 'var(--text-primary)',
                    WebkitTapHighlightColor: 'transparent',
                }}
            >
                {/* Ikona jako na ploše iOS 26: sklo tónované barvou sekce,
                    odlesk na horní hraně. Otevřený modul má prstenec. */}
                <span aria-hidden="true" style={{
                    position: 'relative', width: strana, height: strana, borderRadius: Math.round(strana * 0.27),
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    background: isDark
                        ? `linear-gradient(160deg, ${color}70, ${color}24)`
                        : `linear-gradient(160deg, ${color}45, ${color}18)`,
                    color: isDark ? '#fff' : popredi,
                    boxShadow: (isDark
                        ? 'inset 0 1px 0 rgba(255,255,255,0.28), inset 0 -8px 16px rgba(255,255,255,0.04), 0 6px 14px rgba(0,0,0,0.32)'
                        : 'inset 0 1px 0 rgba(255,255,255,0.9), 0 4px 12px rgba(30,30,50,0.12)')
                        + (aktivni ? `, 0 0 0 2px ${popredi}` : ''),
                }}>
                    <Icon size={husta ? 24 : 28} strokeWidth={2} />
                    {odznak(pocty[item.id] || 0)}
                </span>
                <span style={{
                    maxWidth: '100%', fontSize: husta ? 11 : 12.5, fontWeight: aktivni ? 700 : 600, lineHeight: 1.2,
                    textAlign: 'center', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                    overflow: 'hidden', overflowWrap: 'anywhere',
                }}>
                    {item.label}
                </span>
                {ceka && (
                    <span style={{
                        fontSize: 9, fontWeight: 600, letterSpacing: 0.3,
                        textTransform: 'uppercase', color: 'var(--text-muted)',
                    }}>
                        Připravujeme
                    </span>
                )}
            </button>
        );
    };

    // Nadpis sekce je tichý — barvu nese tónování ikon, nadpis ji jen
    // připomene tečkou. Mezery jsou utažené tak, aby se celé menu vešlo
    // na obrazovku telefonu bez rolování (402 × 874 i s devatenácti moduly).
    const nadpis = (text, color, prvni = false) => (
        <div style={{
            display: 'flex', alignItems: 'center', gap: 7,
            padding: prvni ? '0 4px 8px' : (husta ? '14px 4px 8px' : '20px 4px 10px'),
            fontSize: 11, fontWeight: 700, letterSpacing: 0.8,
            textTransform: 'uppercase', color: 'var(--text-muted)',
        }}>
            {color && <span aria-hidden="true" style={{ width: 6, height: 6, borderRadius: '50%', background: color, flexShrink: 0 }} />}
            {text}
        </div>
    );

    return createPortal((
        <div
            role="dialog"
            aria-modal="true"
            aria-label="Menu"
            style={{
                /* Přes lištu, ne za ní — viz komentář u komponenty. Pod
                   chatem (80) a modály: co si vezme celou obrazovku, má
                   zůstat nad rozcestníkem. */
                position: 'fixed', zIndex: 70, left: 0, right: 0, top: 0, bottom: 0,
                background: pozadiMenu,
                backdropFilter: ROZOSTRENI_NAVIGACE,
                WebkitBackdropFilter: ROZOSTRENI_NAVIGACE,
                display: 'flex', flexDirection: 'column',
                paddingTop: 'var(--hlavicka-mobil)',
                // Pevné rozvržení — tah prstem nic neposouvá (ani stránku pod menu)
                touchAction: 'none', overscrollBehavior: 'none',
                animation: 'mobilni-menu-in 0.18s ease-out',
            }}
        >
            <style>{`
                @keyframes mobilni-menu-in {
                    from { opacity: 0; transform: scale(0.985); }
                    to { opacity: 1; transform: scale(1); }
                }
            `}</style>

            {/* Verze programu — tichý řádek nahoře. Je to údaj o aplikaci,
                ne o uživateli, takže do profilu nepatří. */}
            {verze && (
                <div style={{
                    flexShrink: 0, fontSize: 11, color: 'var(--text-muted)',
                    padding: `0 max(20px, env(safe-area-inset-right)) 0 max(20px, env(safe-area-inset-left))`,
                }}>
                    Verze {verze}{staraInstalace ? ' · přidej appku na plochu znovu' : ''}
                </div>
            )}

            {/* Moduly — pevná plocha bez rolování, obsah se do ní vejde
                zmenšením (viz `prepocet` nahoře). */}
            <div ref={plochaRef} style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
            <div ref={obsahRef} style={{
                /* Lišta je pod menu, takže se pod obsahem drží jen místo pro
                   domovské gesto — ne celá její rezerva. */
                padding: `10px max(16px, env(safe-area-inset-right)) 8px max(16px, env(safe-area-inset-left))`,
            }}>
                {pocet === 0 ? (
                    <p style={{ margin: 0, padding: '14px 4px', fontSize: 14, color: 'var(--text-muted)' }}>
                        Nemáš přidělené žádné moduly.
                    </p>
                ) : (
                    <>
                        {sNadpisy ? sekce.map((s, i) => (
                            <Fragment key={s.id}>
                                {/* Nadpis i popisek dlaždice berou tón podle motivu —
                                    sytá barva sekce má na světlém skle kontrast
                                    2,1–3,5 : 1, tedy pod hranicí čitelnosti. */}
                                {nadpis(s.label, barvaSekce(s, isDark), i === 0)}
                                <div style={mrizka}>
                                    {s.items.map((item) => dlazdice(item, s.color, barvaSekce(s, isDark)))}
                                </div>
                            </Fragment>
                        )) : (
                            <div style={mrizka}>
                                {sekce.flatMap((s) => s.items.map((item) => dlazdice(item, s.color, barvaSekce(s, isDark))))}
                            </div>
                        )}
                    </>
                )}
            </div>
            </div>

            {/* Dole u palce: rychlé akce jako kulatá tlačítka a vedle nich
                zavření. Akce jsou úkony, které nejsou modulem (Nahrát doklad),
                a ikona v brandové barvě je odliší od neutrálního ✕. Kruh ✕
                je tu vždycky: menu překrývá lištu, takže bez něj není kudy ven. */}
            <div style={{
                flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 10,
                padding: `4px max(16px, env(safe-area-inset-right)) calc(12px + env(safe-area-inset-bottom, 0px)) max(16px, env(safe-area-inset-left))`,
            }}>
                {rychleAkce.map((a) => (
                    <SkloKruh key={a.id} Icon={a.Icon} popis={a.label} onClick={() => jdi(a.id)}
                        isDark={isDark} velikost={48} ikona={20} style={{ color: BRAND }} />
                ))}
                <SkloKruh Icon={X} popis="Zavřít" onClick={onClose} isDark={isDark} velikost={48} ikona={20} />
            </div>
        </div>
    ), document.body);
}
